import { Link } from "react-router-dom";
import { ROUTES } from "../Routes";
import { usePageTitle } from "../hooks/usePageTitle";

const HomePage = () => {
  usePageTitle('Mynexo | Social Gaming Entertainment')
  return (
    <main className="main">
      <div className="container">
        <section className="hero">
          <div className="hero-card">
            <div className="kicker">Virtual Play Lounge | Social Play Only</div>

            <h1 className="hero-title">Mynexo</h1>

            <p className="hero-disclaimer">18+ ONLY | VIRTUAL CREDITS | NO CASH VALUE</p>

            <p className="hero-lead">
              Welcome to mynexo.org, where every round is pure entertainment. Enter any chamber,
              spend virtual Credits, reveal outcomes, and track your run history with no deposits,
              no withdrawals, and no real-money rewards.
            </p>

            <div className="hero-actions">
              <Link to={ROUTES.REGISTER} className="btn btn-primary">Open Your Pass +1000 Credits</Link>
              <a href="#chambers" className="btn btn-secondary">Explore Game Chambers</a>
            </div>
          </div>
        </section>

        <section className="notice-banner">
          <div className="notice-card">
            <h2 className="notice-badge">Important Notice</h2>
            <p>
              Mynexo is a social gaming experience for fun only. Credits are virtual,
              hold no monetary value, and cannot be exchanged for money or prizes.
            </p>
          </div>
        </section>

        <section className="section">
          <div className="section-card">
            <h2 className="section-title">Why Guests Keep Returning</h2>

            <div className="panel">
              <ul className="feature-list">
                <li>Fresh visitors receive starter Credits right away</li>
                <li>Cooldown refills keep sessions moving without payments</li>
                <li>Clean HUD tracking for points, bets, and session progress</li>
                <li>Friendly challenge loop focused on skill and timing</li>
                <li>Virtual-only system with zero cash staking</li>
                <li>Smooth access from desktop and mobile paths</li>
              </ul>
            </div>
          </div>
        </section>

        <section className="section" id="chambers">
          <div className="section-card">
            <h2 className="section-title">Choose Your Chamber</h2>

            <div className="games-grid">
              <div className="game-card">
                <div className="game-icon">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white"
                    strokeWidth="2">
                    <circle cx="12" cy="12" r="10" />
                    <path d="M12 6v6l4 2" />
                  </svg>
                </div>
                <h3 className="game-title">Predictor</h3>
                <p className="game-desc">
                  Guess the lucky number and win virtual points. Pure entertainment with no financial
                  risk.
                </p>
                <Link to={ROUTES.PREDICTOR} className="btn btn-primary">Enter Chamber</Link>
              </div>

              <div className="game-card">
                <div className="game-icon">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white"
                    strokeWidth="2">
                    <rect x="4" y="4" width="16" height="16" rx="2" />
                    <circle cx="8" cy="8" r="1.5" fill="white" />
                    <circle cx="16" cy="8" r="1.5" fill="white" />
                    <circle cx="12" cy="12" r="1.5" fill="white" />
                    <circle cx="8" cy="16" r="1.5" fill="white" />
                    <circle cx="16" cy="16" r="1.5" fill="white" />
                  </svg>
                </div>
                <h3 className="game-title">Dice</h3>
                <p className="game-desc">
                  Roll the dice and predict the outcome. Simple, fun, and completely social - no real
                  money involved!
                </p>
                <Link to={ROUTES.DICE} className="btn btn-primary">Enter Chamber</Link>
              </div>

              <div className="game-card">
                <div className="game-icon">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white"
                    strokeWidth="2">
                    <rect x="2" y="6" width="6" height="12" rx="1" />
                    <rect x="9" y="6" width="6" height="12" rx="1" />
                    <rect x="16" y="6" width="6" height="12" rx="1" />
                    <line x1="5" y1="12" x2="5" y2="12" />
                    <line x1="12" y1="12" x2="12" y2="12" />
                    <line x1="19" y1="12" x2="19" y2="12" />
                  </svg>
                </div>
                <h3 className="game-title">Spinners</h3>
                <p className="game-desc">
                  Spin the reels and win virtual points in our custom game. Features multiple paylines and
                  bonus rounds!
                </p>
                <Link to={ROUTES.SPINNERS} className="btn btn-primary">Enter Chamber</Link>
              </div>

              <div className="game-card">
                <div className="game-icon">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white"
                    strokeWidth="2">
                    <rect x="3" y="3" width="14" height="18" rx="2" />
                    <path d="M10 8L7 12l3 4" />
                    <rect x="7" y="3" width="14" height="18" rx="2" fill="none" />
                  </svg>
                </div>
                <h3 className="game-title">Card Game</h3>
                <p className="game-desc">
                  Test your card skills against the house in this classNameic game. All winnings are virtual
                  points only.
                </p>
                <Link to={ROUTES.CARD_GAME} className="btn btn-primary">Enter Chamber</Link>
              </div>
            </div>
          </div>
        </section>

        <section className="section">
          <div className="section-card">
            <h2 className="section-title">The Game Loop at Mynexo</h2>

            <div className="panel">
              <p style={{ margin: 0 }}>
                Pick a chamber, place a virtual Credit stake, reveal the result, and continue your run.
                Every outcome updates your session board so your progress stays visible.
              </p>
              <p style={{ margin: '0.85rem 0 0' }}>
                New visitors begin with starter Credits and refill windows are available later.
                No deposits are required, and Credits never convert to real-world money.
              </p>
            </div>
          </div>
        </section>

        <section className="section">
          <div className="section-card">
            <h2 className="section-title">Quick Questions</h2>

            <div className="faq-grid">
              <div className="faq-item">
                <h3>Is this real-money gambling?</h3>
                <p>No. It is social entertainment with virtual Credits only.</p>
              </div>

              <div className="faq-item">
                <h3>Do I have to pay to play?</h3>
                <p>No deposits and no purchases are required to enjoy the games.</p>
              </div>

              <div className="faq-item">
                <h3>How does one run work?</h3>
                <p>Choose a game, set a Credit amount, confirm your move, and review the result.</p>
              </div>

              <div className="faq-item">
                <h3>What are Credits?</h3>
                <p>Credits are virtual points used inside the site and cannot be withdrawn.</p>
              </div>
            </div>
          </div>
        </section>

        <section className="section">
          <div className="section-card">
            <h2 className="section-title">Send a Message</h2>
            <p className="text-center" style={{ color: 'var(--text-medium)', marginBottom: '24px' }}>
              Share feedback, report a bug, send an idea, or open a partnership conversation.
              We read every message carefully.
            </p>

            <form id="contactForm" className="contact-form">
              <div className="form-group">
                <label htmlFor="contact-name">Name</label>
                <input type="text" id="contact-name" name="name" placeholder="Your display name" />
              </div>

              <div className="form-group">
                <label htmlFor="contact-email">Email</label>
                <input type="email" id="contact-email" name="email" placeholder="you@example.com" />
              </div>

              <div className="form-group">
                <label htmlFor="contact-message">Message</label>
                <textarea id="contact-message" name="message"
                  placeholder="Tell us what happened, what you expected, and any useful details."></textarea>
              </div>

              <button type="submit" className="submit-btn">Send Message</button>
            </form>
          </div>
        </section>
      </div>
    </main>
  );
};

export default HomePage;